import { useState } from 'react'
import { Download, Eye, Pencil, Plus, Trash2 } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Button } from '@/components/ui/button'
import { UploadDocumentModal } from '@/components/documents/upload-document-modal'
import { DocumentRequestsTab } from '@/components/document-requests/document-requests-tab'
import { useDeleteDocument, useDocuments } from '@/hooks/use-documents'
import { useTabQueryParam } from '@/hooks/use-tab-query-param'
import { downloadDocument, isViewableMimeType, viewDocument } from '@/lib/documents'
import { formatDate, cn } from '@/lib/utils'
import type { DocumentCategory, EstateDocument } from '@riverpark/types'

const CATEGORY_LABEL: Record<DocumentCategory, string> = {
  agm_minutes: 'AGM minutes',
  financial_report: 'Financial report',
  notice: 'Notice',
  title_deed: 'Title deed',
  compliance: 'Compliance',
  other: 'Other',
}

function formatFileSize(bytes: number | null): string {
  if (bytes === null) return '—'
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function visibilityLabel(doc: { visibility: string; plotNumber: string | null }): string {
  if (doc.visibility === 'public') return 'Public'
  if (doc.visibility === 'all_members') return 'All members'
  if (doc.visibility === 'specific_plot') return `Plot ${doc.plotNumber ?? '—'}`
  return 'One member'
}

type Tab = 'library' | 'requests'
const TABS: { id: Tab; label: string }[] = [
  { id: 'library', label: 'Library' },
  { id: 'requests', label: 'Requests' },
]
const VALID_TABS: Tab[] = ['library', 'requests']

export function AdminDocuments() {
  const [tab, setTab] = useTabQueryParam(VALID_TABS, 'library')
  const { data, isLoading } = useDocuments({ limit: 100 }, tab === 'library')
  const deleteDoc = useDeleteDocument()
  const [uploading, setUploading] = useState(false)
  const [editing, setEditing] = useState<EstateDocument | null>(null)
  const items = data?.data ?? []

  return (
    <PageShell>
      <PageHeader
        label="Content"
        title="Documents"
        description="Upload and manage estate documents with visibility controls."
        action={
          tab === 'library' ? (
            <Button size="sm" onClick={() => setUploading(true)}>
              <Plus size={14} className="mr-1.5" />
              Upload document
            </Button>
          ) : undefined
        }
      />

      <div className="mb-6 flex gap-1 border-b border-border-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'px-4 py-2.5 font-sans text-sm font-medium transition-colors',
              tab === t.id
                ? 'border-b-2 border-primary text-ink'
                : 'text-muted-foreground hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'requests' ? (
        <DocumentRequestsTab />
      ) : isLoading ? (
        <p className="font-sans text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
          No documents uploaded yet.
        </p>
      ) : (
        <div className="overflow-x-auto rounded border border-border-2 bg-card">
          <table className="w-full font-sans text-sm">
            <thead>
              <tr className="border-b border-border-2 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-2.5">Title</th>
                <th className="px-4 py-2.5">Category</th>
                <th className="px-4 py-2.5">Visible to</th>
                <th className="px-4 py-2.5">Size</th>
                <th className="px-4 py-2.5">Uploaded</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {items.map((doc) => (
                <tr key={doc.id} className="border-b border-border-2 last:border-0">
                  <td className="px-4 py-2.5 font-medium text-ink">{doc.title}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {CATEGORY_LABEL[doc.category]}
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">{visibilityLabel(doc)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {formatFileSize(doc.sizeBytes)}
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {formatDate(new Date(doc.createdAt * 1000))}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {isViewableMimeType(doc.mimeType) && (
                        <button
                          type="button"
                          onClick={() => viewDocument(doc)}
                          className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
                          aria-label="View"
                        >
                          <Eye size={16} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => downloadDocument(doc)}
                        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
                        aria-label="Download"
                      >
                        <Download size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing(doc)}
                        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
                        aria-label="Edit"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Delete "${doc.title}"? This cannot be undone.`)) {
                            deleteDoc.mutate(doc.id)
                          }
                        }}
                        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                        aria-label="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {uploading && <UploadDocumentModal onClose={() => setUploading(false)} />}
      {editing && <UploadDocumentModal document={editing} onClose={() => setEditing(null)} />}
    </PageShell>
  )
}
